import { AxiosError } from 'axios'
import findIndex from 'lodash/findIndex'
import isArray from 'lodash/isArray'
import { action, computed, flow, makeObservable, observable, toJS } from 'mobx'

import { requestWrapper } from 'Shared/utils'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator } from 'Core/interfaces'
import { getDefaultStoreArrayValue } from 'Core/utils'

import { STORE_AUDIT } from 'Portals/Customer/constants'
import { Kpi, KpiMetricFilterFields } from 'Portals/Customer/entities'
import { KpiRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import { formatKpiMetricFilterOptions } from 'Portals/Customer/utils'

import { KpiTable } from './KpiTable'

export class KpiStore {
	table: KpiTable

	@observable accessor kpiMeasure = getDefaultStoreArrayValue<Kpi.KpiLocationMeasureForm>()
	@observable accessor selectedKpiId: number | null = null
	@observable accessor savedMeasureData: Kpi.SavedMeasureData[] = []
	@observable accessor successfullySavedId: Key | null = null
	@observable accessor kpiMetricFilterOptions = getDefaultStoreArrayValue<Kpi.Metric.FilterOptions>()
	@observable accessor dataEntryDirtyConfig: Kpi.DirtyConfig[] = []
	@observable accessor loadingRowId: number | null = null

	constructor(public rootStore: RootStore) {
		this.table = new KpiTable(rootStore)
	}

	@computed
	get measure() {
		return toJS(this.kpiMeasure.value)
	}

	@computed
	get isLoadingData() {
		return this.rootStore[STORE_AUDIT].dailyCount.isLoading || this.kpiMeasure.isLoading
	}

	@computed
	get kpiMetricOptions() {
		return formatKpiMetricFilterOptions(this.kpiMetricFilterOptions.value)
	}

	@computed
	get kpiOptions() {
		const selectedCategory = this.table.filters[KpiMetricFilterFields.kpiCategoryMetric]?.data
		// because of implementation of filters. First load always array
		const correctValue = isArray(selectedCategory) ? selectedCategory[0] : selectedCategory
		return this.kpiMetricOptions.find((opt) => opt.value === correctValue)?.kpiOptions || []
	}

	@computed
	get dirtyConfig() {
		return toJS(this.dataEntryDirtyConfig)
	}

	@flow.bound
	*addKpi(payload: Kpi.Request.Add): CustomGenerator<Kpi.MeasureResponse> {
		const { rowKey, ...rest } = payload
		this.selectedKpiId = null
		this.loadingRowId = rowKey
		try {
			const result = yield KpiRepo.addKpi(rest)
			this.savedMeasureData = [
				...this.savedMeasureData,
				{ measureId: result.measureId, rowKey: payload.rowKey }
			]
			this.successfullySavedId = payload.rowKey
			this.rootStore[STORE_AUDIT].retrieveSuccessfullySavedCount()
		} catch (error) {
			this.successfullySavedId = null
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*editKpi(payload: Kpi.Request.Edit, kpiId: number, measureId: number, rowKey: number) {
		this.selectedKpiId = null
		this.loadingRowId = rowKey
		try {
			yield KpiRepo.editKpi(payload, kpiId, measureId)
			this.successfullySavedId = rowKey
			this.rootStore[STORE_AUDIT].retrieveSuccessfullySavedCount()
		} catch (error) {
			this.successfullySavedId = null
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*retrieveKpiMeasureData(
		payload: Kpi.MeasurePayload
	): CustomGenerator<Kpi.KpiLocationMeasure[]> {
		const locId = this.table.serviceSiteId as number
		const { rowKey, ...rest } = payload
		const correctRequestData: Kpi.MeasureRequest = {
			...rest,
			serviceSiteId: locId || payload.serviceSiteId
		}
		this.loadingRowId = null
		this.kpiMeasure.isLoading = true
		this.selectedKpiId = payload.rowKey
		try {
			// always will be 1 element
			const measure = yield KpiRepo.retrieveKpiMeasureData(correctRequestData)

			if (!!measure.length) {
				const updatedMeasure = {
					...measure[0],
					rowKey: payload.rowKey
				}

				const index = findIndex(
					this.kpiMeasure.value,
					(val) => val.rowKey === payload.rowKey
				)
				if (index !== -1) {
					this.kpiMeasure.value[index] = updatedMeasure
					return
				}
				this.kpiMeasure.value.push(updatedMeasure)
			} else {
				this.kpiMeasure.value = this.kpiMeasure.value.filter(
					(val) => val.rowKey !== payload.rowKey
				)
			}
			this.savedMeasureData = this.savedMeasureData.filter(
				(val) => val.rowKey !== payload.rowKey
			)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
			this.selectedKpiId = null
		} finally {
			this.kpiMeasure.isLoading = false
		}
	}

	@action.bound
	resetKpiConfigs() {
		this.selectedKpiId = null
		this.successfullySavedId = null
		this.savedMeasureData = []
		this.kpiMeasure = getDefaultStoreArrayValue()
	}

	@action.bound
	getIsLoadingRow(id: number, isLoading: boolean) {
		return isLoading && (id === this.loadingRowId || id === this.selectedKpiId)
	}

	@flow.bound
	*retrieveMetricFilterOptions() {
		yield requestWrapper({
			field: this.kpiMetricFilterOptions,
			repository: KpiRepo.retrieveMetricFilterOptions,
			repositoryParams: null
		})
	}

	@action.bound
	updateDirtyConfig(rowKey: number, isDirty: boolean) {
		const result = { rowKey: rowKey, isDirty }
		const index = findIndex(this.dataEntryDirtyConfig, (val) => val.rowKey === rowKey)
		if (index !== -1) {
			this.dataEntryDirtyConfig[index] = result
			return
		}
		this.dataEntryDirtyConfig = [...this.dataEntryDirtyConfig, result]
	}

	@action.bound
	resetDirtyConfig() {
		this.dataEntryDirtyConfig = []
	}

	@action.bound
	setDefaultMetricFilters(kpiMetricOptions: Kpi.Metric.FormattedFilterOptions[]) {
		const kpiCategoryDefaultValue = kpiMetricOptions.find(
			(opt) => opt.label === 'Hours and Headcount'
		)

		const kpiMetricCategoryFilter =
			this.table.filters[KpiMetricFilterFields.kpiCategoryMetric] || null

		if (!kpiCategoryDefaultValue || kpiMetricCategoryFilter?.data) {
			return
		}
		this.table.setFilter({
			...kpiMetricCategoryFilter,
			data: kpiCategoryDefaultValue?.value
		})
	}
}
